import { components } from '../../../generated/api/schema';
import type { UserRole } from './user';
import type { PassengerSearchPermissions } from './user-v2';

export type PassengerAddress = components['schemas']['PassengerAddress'];

export enum ScheduleState {
  PendingRemoval = '-3',
  Edited = '-2',
  NotApproved = '-1',
  Approved = '0',
  Complete = '1',
}

export enum WeekKind {
  Any = 'ALL',
  Odd = 'ODD',
  Even = 'EVEN',
  Specific = 'SPECIFIED',
  Calendar = 'Calendar',
}

export enum ScheduleLineType {
  Date = 'DATE',
}

export type PassengerSearchRequest =
  components['schemas']['PassengerSearchRequest'];
export type PassengerSearchResponse = Omit<components['schemas']['PassengerSearchResponseDto'], 'permissions'> & {
  permissions?: PassengerSearchPermissions;
};
export type PassengerDto = Omit<components['schemas']['PassengerDto'], 'agreement'> & {
  agreement: components['schemas']['AgreementDto'] | null;
  institutionIds?: string[];
};
export type UserPassengerDto = components['schemas']['UserPassengerSummaryDto'];

export type AssignedUserRole = Exclude<UserRole, 'NONE'>;

export interface AssignedUserDto {
  id?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  role?: AssignedUserRole | null;
}

export type PassengerDetailsDto = Omit<
  components['schemas']['PassengerDetailsDto'],
  'dateOfBirth'
> & {
  /** Temporary extension until ENB-2355 is published in the generated API schema. */
  dateOfBirth?: string | null;
};

export type CreatePassengerRequest = Omit<
  components['schemas']['CreatePassengerRequest'],
  'publicId' | 'dateOfBirth'
> & {
  /** ISO local date (yyyy-MM-dd). The backend generates publicId from this value and the name. */
  dateOfBirth: string;
};
export type CreatePassengerResponse =
  components['schemas']['CreatePassengerResponse'];

export type UpdatePassengerDetailsRequest = Omit<
  components['schemas']['UpdatePassengerDetailsRequest'],
  'dateOfBirth'
> & {
  dateOfBirth?: string | null;
};
export type UpdatePassengerDetailsResponse = PassengerDetailsDto | undefined;

export type Schedule = components['schemas']['Schedule'];
export type ScheduleWriteDto = components['schemas']['ScheduleWriteDto'];

export type ScheduleLine = components['schemas']['ScheduleLine'];
export type ScheduleLineWeekPattern =
  components['schemas']['ScheduleLine']['weekPattern'];

export type ScheduleCalendarLine =
  components['schemas']['ScheduleCalendarLine'];
export type ScheduleCalendarLineWriteDto =
  components['schemas']['ScheduleCalendarLineWriteDto'];

export type CreateScheduleCalendarLine = ScheduleCalendarLine;

export type PassengerDetailsStatus = NonNullable<PassengerDto['status']>;

/**
 * Passenger detail statuses used by the UI.
 * This list is manually maintained and type-checked against the generated API schema type.
 */
export const PASSENGER_DETAIL_STATUSES: PassengerDetailsStatus[] = [
  'ACTIVE',
  'INACTIVE',
  'AWAITING_APPROVAL',
  'COMPLETED',
];

export type NeedDto = components['schemas']['AttributeDto'];
export type OperationalAttributeDto = components['schemas']['AttributeDto'];
export type AttributeDto = components['schemas']['AttributeDto'];
export type AddressTypeDto = components['schemas']['AddressTypeDto'];
export type ContactTypeDto = components['schemas']['ContactTypeDto'];

// V2 new endpoints
export type PendingReviewPassengerDto =
  components['schemas']['PendingReviewPassengerDto'];
export type AbsenceRequest = components['schemas']['AbsenceRequest'];
export type AbsenceResponse = components['schemas']['AbsenceResponse'];
export type Absence = components['schemas']['Absence'];
export type ScheduleTypeDto = components['schemas']['ScheduleTypeDto'];

/** Response from GET /v2/passengers/schedules/{scheduleId}/pending-review */
export interface SchedulePendingChangeEntry {
  scheduleId: number;
  status: number;
  modifiedAt: number;
  pendingChanges: Partial<Schedule> | null;
}

export type ScheduleAction = 'approve' | 'reject';

export interface SchedulePendingReviewResponse {
  passengerId: number;
  firstName: string;
  lastName: string;
  passengerStatus: string;
  schedules: SchedulePendingChangeEntry[];
}

/** Response from GET /v2/passengers/{passengerId}/pending-review */
export interface PassengerPendingChanges {
  previousStatus?: PassengerDetailsStatus;
  targetStatus?: PassengerDetailsStatus;
  agreementId?: number | null;
  attributes?: AttributeDto[] | null;
  driverInstructions?: string | null;
}

export interface PassengerPendingReviewResponse {
  passengerId?: number;
  firstName?: string;
  lastName?: string;
  currentStatus?: PassengerDetailsStatus;
  currentAgreementId?: number;
  currentAttributes?: AttributeDto[];
  currentDriverInstructions?: string;
  pendingChanges?: PassengerPendingChanges | null;
}
